import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { PostCard } from 'src/charts/IndividualPostsSection/PostCard'
import { DeltaBadge } from 'src/charts/TopicsSubtopics/DeltaBadge'
import {
  MentionsPlot,
  PlatformDonut,
  SentimentDonut,
  SubtopicBar,
} from 'src/charts/TopicsSubtopics/TopicDetailModal/parts'
import styles from 'src/charts/TopicsSubtopics/TopicDetailModal/topicDetailModal.module.scss'
import { CONTENT_TOPICS_COPY } from 'src/charts/TopicsSubtopics/copy'
import { axisMax, axisTicks } from 'src/interfaces/topics.interface'
import { useGetContentAnalysisTopicDetailQuery } from 'src/reactQuery/useGetContentAnalysisTopicDetailQuery'
import { IndividualPost } from 'src/services/__generated__/api'
import {
  ContentAnalysisV2Scope,
  ContentCommentRow,
  ContentPostCard,
  ContentTopicsBy,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'
import local from './contentTopics.module.scss'
import { CommentRow } from '../CommentRow'
import { percent, platformColor } from '../shared'

interface ContentTopicDetailModalProps {
  by: ContentTopicsBy
  onClose: () => void
  scope: ContentAnalysisV2Scope
  // List page: related comments name the analysis they came from.
  showCampaign?: boolean
  topic: string
}

const PAGE_SIZE = 5

const LEGEND_ITEMS = [
  { color: '#8080FF', key: 'positive', label: 'Positive' },
  { color: '#C7CED1', key: 'neutral', label: 'Neutral' },
  { color: '#FF6833', key: 'negative', label: 'Negative' },
]

// "2026-10-12" → "12 Oct" ("12 Oct 2026" when the series spans years). Read
// as UTC: the series is already bucketed by day on the server, and a
// local-time parse would move it a day west of UTC.
const trendLabel = (date: string, withYear = false) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  })

const spansYears = (trend: { date: string }[]) =>
  trend.length > 0 &&
  trend[0].date.slice(0, 4) !== trend[trend.length - 1].date.slice(0, 4)

// The Individual Posts card reads the generated post shape.
// Counts the platform did not report stay unknown (undefined), not 0.
const toIndividualPost = (post: ContentPostCard): IndividualPost =>
  ({
    commentsCount: post.commentsCount ?? undefined,
    content: post.content ?? '',
    date: post.date ? moment(post.date).format('YYYY-MM-DD h:mm A') : '',
    id: `${post.platform}|${post.postUrl ?? ''}`,
    imageUrl: post.imageUrl ?? undefined,
    platform: post.platform as IndividualPost['platform'],
    postUrl: post.postUrl ?? undefined,
    reactions: post.reactions ?? undefined,
    reposts: post.reposts ?? undefined,
    shares: post.shares ?? undefined,
    views: post.views,
  }) as IndividualPost

const isPost = (
  item: ContentPostCard | ContentCommentRow
): item is ContentPostCard => (item as ContentPostCard).kind === 'post'

const Chevron: FC<{ left?: boolean }> = ({ left = false }) => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <path
      d={left ? 'M10 3.5L5.5 8L10 12.5' : 'M6 3.5L10.5 8L6 12.5'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

/**
 * Topic and Subtopic popup for Content Analysis. Built from the Topic Detail
 * modal's parts (shell, sentiment donut, subtopic bars, trend plot), with what
 * this product differs on: a platform donut, one trend for the page range (no
 * 7/14/30-day tabs), and related posts as Individual Posts cards or, for a
 * comment topic, comment rows, paged.
 */
export const ContentTopicDetailModal: FC<ContentTopicDetailModalProps> = ({
  by,
  onClose,
  scope,
  showCampaign = false,
  topic,
}) => {
  const copy = CONTENT_TOPICS_COPY[by]
  const [subtopic, setSubtopic] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  // The open topic's count, kept when drilling into one of its subtopics:
  // "% of topic" is the subtopic's count over it. sm.back's `share` is over
  // every tagged item, which is "% of total", not this.
  const [topicCount, setTopicCount] = useState(0)
  const { data, isError, isFetching } = useGetContentAnalysisTopicDetailQuery(
    scope,
    {
      by,
      limit: PAGE_SIZE,
      name: subtopic ?? topic,
      page,
      parent: subtopic ? topic : null,
    }
  )
  // The query keeps the previous answer while the next loads; only draw it
  // when it describes the view on screen.
  const matches =
    !!data &&
    data.name === (subtopic ?? topic) &&
    (data.parent ?? null) === (subtopic ? topic : null)
  const pageMatches = matches && data!.related.page === page
  const inSubtopic = !!subtopic
  const subtopics = matches
    ? data!.subtopics.map((row) => ({
        ...row,
        shareOfTopic: percent(row.count, data!.count),
      }))
    : []
  const subtopicMax = axisMax(subtopics)
  const totalPages = matches
    ? Math.max(1, Math.ceil(data!.related.total / PAGE_SIZE))
    : 1

  const openSubtopic = (name: string | null) => {
    if (name && matches && !inSubtopic) setTopicCount(data!.count)
    setSubtopic(name)
    setPage(1)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className={styles.overlay}
      onClick={onClose}
      onKeyDown={undefined}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        <div className={styles.header}>
          {inSubtopic ? (
            <h2 className={`${styles.title} ${styles.breadcrumb}`}>
              <button
                aria-label="Back to topic"
                className={styles.backButton}
                onClick={() => openSubtopic(null)}
                type="button"
              >
                <Chevron left />
              </button>
              <button
                className={styles.crumbMuted}
                onClick={() => openSubtopic(null)}
                type="button"
              >
                Topic
              </button>
              <span className={styles.crumbSep}>›</span>
              <span>Subtopic</span>
            </h2>
          ) : (
            <h2 className={styles.title}>Topic</h2>
          )}
          <button
            aria-label="Close"
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
              <path
                d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.6"
              />
            </svg>
          </button>
        </div>

        <div className={styles.splitter} />

        <div className={styles.body}>
          {isError && !matches && (
            <span className={styles.cardEmpty}>
              This topic could not be loaded. Please try again later.
            </span>
          )}
          {!isError && !matches && (
            <div className={styles.loading}>
              <span className={styles.loadingBlock} />
              <div className={styles.summaryRow}>
                <span
                  className={`${styles.summaryCard} ${styles.loadingBlock}`}
                />
                <span
                  className={`${styles.summaryCard} ${styles.loadingBlock}`}
                />
              </div>
              <span
                className={`${styles.loadingBlock} ${styles.loadingTall}`}
              />
            </div>
          )}
          {matches && (
            <>
              <div className={styles.topicRow}>
                {inSubtopic && (
                  <span className={styles.topicParent} title={topic}>
                    {topic}
                    <span className={styles.crumbSep}>›</span>
                  </span>
                )}
                <span className={styles.topicName}>{data!.name}</span>
                {data!.delta !== null && (
                  <span
                    className={`${styles.topicDelta} ${
                      data!.delta < 0 ? styles.topicDeltaDown : ''
                    }`}
                  >
                    <DeltaBadge value={data!.delta} />
                  </span>
                )}
              </div>

              {!!data!.definition && (
                <p
                  className={`${styles.topicSummary} ${styles.topicSummaryNeutral}`}
                >
                  {data!.definition}
                </p>
              )}

              <div className={styles.summaryRow}>
                <div className={styles.summaryCard}>
                  <SentimentDonut
                    mentions={data!.count}
                    noun={copy.noun}
                    nounSingular={copy.nounSingular}
                    sentiment={data!.sentiment}
                    shareLabel={inSubtopic ? 'of topic' : copy.shareOfTotal}
                    shareOfTotal={
                      inSubtopic
                        ? percent(data!.count, topicCount)
                        : data!.share
                    }
                    totalLabel={copy.totalLabel}
                  />
                </div>
                <div className={styles.summaryCard}>
                  <PlatformDonut
                    colorOf={platformColor}
                    noun={copy.noun}
                    nounSingular={copy.nounSingular}
                    platforms={data!.platforms}
                  />
                </div>
              </div>

              {!inSubtopic && (
                <div className={styles.subtopics}>
                  <div className={styles.subHeader}>
                    <span className={styles.subTitle}>
                      Subtopics {subtopics.length}{' '}
                      <span className={styles.subTitleMuted}>Identified</span>
                    </span>
                    <span className={styles.donutLegend}>
                      {LEGEND_ITEMS.map((item) => (
                        <span className={styles.legendItem} key={item.key}>
                          <span
                            className={styles.legendDot}
                            style={{ background: item.color }}
                          />
                          {item.label}
                        </span>
                      ))}
                    </span>
                  </div>

                  <div className={styles.subRows}>
                    {subtopics.map((sub) => (
                      <div
                        className={styles.subRow}
                        key={sub.name}
                        onClick={() => openSubtopic(sub.name)}
                        onKeyDown={(event) => {
                          if (
                            event.target === event.currentTarget &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault()
                            openSubtopic(sub.name)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <span>
                          <span className={styles.subName}>{sub.name}</span>
                          <span className={styles.subMeta}>
                            <strong>
                              {`${sub.count.toLocaleString()} ${
                                sub.count === 1 ? copy.nounSingular : copy.noun
                              }`}
                            </strong>
                            {` · ${sub.shareOfTopic}% of topic`}
                          </span>
                        </span>
                        <SubtopicBar
                          max={subtopicMax}
                          mentions={sub.count}
                          sentiment={sub.sentiment}
                          share={sub.shareOfTopic}
                          totalLabel={copy.totalLabel}
                        />
                        {sub.delta === null ? (
                          <span />
                        ) : (
                          <span
                            className={`${styles.subDelta} ${
                              sub.delta < 0 ? styles.subDeltaDown : ''
                            }`}
                          >
                            <DeltaBadge value={sub.delta} />
                          </span>
                        )}
                        <span className={styles.subChevron}>
                          <Chevron />
                        </span>
                      </div>
                    ))}
                  </div>

                  {subtopics.length === 0 ? (
                    <span className={styles.cardEmpty}>
                      No subtopics have been identified for this topic yet
                    </span>
                  ) : (
                    <div className={styles.subAxis}>
                      <span className={styles.subAxisTicks}>
                        {axisTicks(subtopicMax).map((tick) => (
                          <span className={styles.subAxisTick} key={tick}>
                            {tick}%
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!inSubtopic && (
                <div className={styles.mentions}>
                  <div className={styles.mentionsHeader}>
                    <span className={styles.mentionsLabel}>
                      <svg
                        aria-hidden="true"
                        className={styles.mentionsLabelMark}
                        fill="none"
                        viewBox="0 0 20 10"
                      >
                        <path
                          d="M1 5H17"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <circle
                          cx="9"
                          cy="5"
                          fill="#FFFFFF"
                          r="3"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                      {copy.noun.charAt(0).toUpperCase() + copy.noun.slice(1)}
                    </span>
                  </div>
                  <div className={styles.mentionsPlot}>
                    <MentionsPlot
                      emptyLabel={`No ${copy.noun} in this period`}
                      points={data!.trend.map((point) => ({
                        label: trendLabel(point.date, spansYears(data!.trend)),
                        value: point.count,
                      }))}
                    />
                  </div>
                </div>
              )}

              <div className={styles.relatedHeader}>
                <span className={styles.relatedTitle}>{copy.relatedTitle}</span>
                <span className={styles.relatedHint}>
                  {inSubtopic
                    ? copy.relatedHintSubtopic
                    : copy.relatedHintTopic}
                </span>
              </div>
              <div
                className={`${styles.relatedList} ${
                  isFetching && !pageMatches ? local.relatedFetching : ''
                }`}
              >
                {data!.related.items.length === 0 ? (
                  <div className={styles.relatedEmpty}>
                    <span className={styles.relatedEmptyTitle}>
                      {`No ${copy.noun} linked to this ${
                        inSubtopic ? 'subtopic' : 'topic'
                      } yet`}
                    </span>
                    <span className={styles.relatedEmptyHint}>
                      {copy.relatedEmptyHint}
                    </span>
                  </div>
                ) : (
                  // Keys carry the position: the same post or comment can come
                  // back twice (a URL shared by two analyses, a null postUrl).
                  data!.related.items.map((item, index) =>
                    isPost(item) ? (
                      <PostCard
                        key={`${item.analysisId}|${item.platform}|${
                          item.postUrl ?? ''
                        }|${index}`}
                        post={toIndividualPost(item)}
                        variant="vertical"
                      />
                    ) : (
                      <CommentRow
                        comment={item}
                        key={`${item.analysisId}|${item.key}|${index}`}
                        showCampaign={showCampaign}
                      />
                    )
                  )
                )}
              </div>
              {totalPages > 1 && (
                <div className={local.pager}>
                  <span className={local.pagerText}>
                    {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(
                      page * PAGE_SIZE,
                      data!.related.total
                    )} of ${data!.related.total.toLocaleString()}`}
                  </span>
                  <button
                    aria-label="Previous page"
                    className={local.pagerButton}
                    disabled={page <= 1}
                    onClick={() => setPage((value) => value - 1)}
                    type="button"
                  >
                    <Chevron left />
                  </button>
                  <button
                    aria-label="Next page"
                    className={local.pagerButton}
                    disabled={page >= totalPages}
                    onClick={() => setPage((value) => value + 1)}
                    type="button"
                  >
                    <Chevron />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
